import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackCallClick } from "../utils/analytics";
import "../styles/LocationPage.css";
import LocationHero from "../components/LocationHero";
import { Location, getNavSections, getSectionLabel } from "../data/menuConfig";

interface LocationPageProps {
  location: Location;
}

const LocationPage: React.FC<LocationPageProps> = ({ location }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as "en" | "fr";

  const locationData = {
    montreal: {
      name: "MONTRÉAL",
      hours: t("locations.montreal.hours"),
      address:
        currentLang === "fr"
          ? "2532 rue Ste-Catherine E\nMontréal, QC H2K 2K2"
          : "2532 St Catherine St E\nMontreal, QC H2K 2K2",
      phone: "514-527-2532",
      phoneLink: "tel:5145272532",
    },
    delson: {
      name: "DELSON",
      hours: t("locations.delson.hours"),
      address: "35 Boulevard Georges-Gagné Sud\nDelson, QC J5B 2E4",
      phone: "450-632-5320",
      phoneLink: "tel:4506325320",
    },
  };

  const data = locationData[location];
  const locationName = data.name.toLocaleLowerCase();
  const locationNameCamelCase =
    locationName.charAt(0).toUpperCase() + locationName.slice(1);

  return (
    <div className="location-page">
      {/* Hero Section */}
      <LocationHero
        locationName={data.name}
        description={t("location.description", {
          location: locationNameCamelCase,
        })}
        hours={data.hours}
        address={data.address}
        phone={data.phone}
        phoneLink={data.phoneLink}
        variant="location"
      />

      {/* Menu Section */}
      <section className="location-menu-section">
        <div className="container">
          <h2 className="menu-section-title">{t("menu.title")}</h2>

          <div className="menu-categories">
            {getNavSections(location).map((section, index) => (
              <React.Fragment key={section}>
                <Link
                  to={`/${location}/menu/${section}`}
                  className="menu-category-link"
                >
                  {getSectionLabel(location, section, currentLang)}
                </Link>
                {index < getNavSections(location).length - 1 && (
                  <span className="menu-cat-divider">|</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Dishes Section */}
      <section className="featured-dishes-section">
        <div className="container">
          <h2 className="featured-title">{t("location.featuredTitle")}</h2>
          <p className="featured-subtitle">{t("location.featuredSubtitle")}</p>

          <div className="featured-dishes-grid">
            <div className="featured-dish-card">
              <div className="featured-dish-icon">🥙</div>
              <h3 className="featured-dish-name">
                {t("featured.souvlaki.name")}
              </h3>
              <p className="featured-dish-description">
                {t("featured.souvlaki.description")}
              </p>
            </div>

            <div className="featured-dish-card">
              <div className="featured-dish-icon">🍖</div>
              <h3 className="featured-dish-name">{t("featured.gyros.name")}</h3>
              <p className="featured-dish-description">
                {t("featured.gyros.description")}
              </p>
            </div>

            <div className="featured-dish-card">
              <div className="featured-dish-icon">🥗</div>
              <h3 className="featured-dish-name">
                {t("featured.greekSalad.name")}
              </h3>
              <p className="featured-dish-description">
                {t("featured.greekSalad.description")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container">
          <h2 className="about-title">{t("location.aboutTitle")}</h2>
          <div className="about-content">
            <p className="about-text">
              {t("location.aboutText1", { location: locationNameCamelCase })}
            </p>
            <p className="about-text">{t("location.aboutText2")}</p>
            <p className="about-text">{t("location.aboutText3")}</p>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="location-cta-section">
        <div className="container">
          <h2 className="location-cta-title">{t("common.readyToOrder")}</h2>
          <p className="location-cta-subtitle">
            {t("common.experienceGreekCuisine")}
          </p>
          <div className="location-cta-buttons">
            <Link to="/order" className="btn-cta-primary">
              {t("common.orderNow").toUpperCase()}
            </Link>
            <a
              href={data.phoneLink}
              className="btn-cta-secondary"
              onClick={() => trackCallClick(location, data.phone)}
            >
              {t("common.call")} {data.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocationPage;
